import type {
  ChangesInbox,
  DocumentList,
  ProjectGroupGrant,
  ProjectMemberView,
  ProjectSummary,
  ProjectView,
  WikiIndex,
} from "@esi/shared";
import { can } from "../capabilities.ts";

/** The Wiki section's figures, over the pages the viewer can read. */
export interface WikiFigures {
  pages: number;
  /** Change sets and review items waiting on the viewer; null without `wiki.view_drafts`. */
  waiting: number | null;
  /** The latest publication the viewer can read (the index log is newest first). */
  lastPublishedAt: string | null;
  /** Pages stale or unverified since their last version (design D9). */
  needsChecking: number;
}

/** Slice A's derivation, while the summary route is missing (one release). */
export function wikiFigures(index: WikiIndex, review: ChangesInbox | null): WikiFigures {
  return {
    pages: index.pages.length,
    // The review count's own predicate, as the Wiki tab's badge counts it.
    waiting: review ? review.count : null,
    lastPublishedAt: index.log[0]?.publishedAt ?? null,
    needsChecking: index.pages.filter((p) => p.check !== undefined).length,
  };
}

/** The Wiki figures from the summary: its counts are the lists' own (a parity test holds it). */
export function wikiFiguresOf(wiki: ProjectSummary["wiki"]): WikiFigures {
  return {
    pages: wiki.pages,
    waiting: wiki.waiting ?? null,
    lastPublishedAt: wiki.lastPublishedAt,
    needsChecking: wiki.needsChecking,
  };
}

/** The Sources section's figures, over the files the viewer can read. */
export interface SourcesFigures {
  files: number;
  processing: number;
  /** The viewer's own failed uploads, or every readable one for `file.manage_any`. */
  failed: number;
  /** Files the viewer cannot read: only with `file.restricted_count`, never for a guest. */
  restricted: number | null;
}

export function sourcesFigures(
  list: DocumentList,
  project: Pick<ProjectView, "capabilities">,
  userId: string,
): SourcesFigures {
  const me = userId.toLowerCase();
  const all = can(project, "file.manage_any");
  let processing = 0;
  let failed = 0;
  for (const d of list.documents) {
    if (d.status === "processing" || d.status === "uploading") {
      processing++;
    } else if (d.status === "failed" && (all || d.uploadedBy.toLowerCase() === me)) {
      failed++;
    }
  }
  return {
    files: list.documents.length,
    processing,
    failed,
    restricted: can(project, "file.restricted_count") ? list.restricted : null,
  };
}

/** The Sources figures from the summary; the restricted count only for `file.restricted_count`. */
export function sourcesFiguresOf(
  sources: ProjectSummary["sources"],
  project: Pick<ProjectView, "capabilities">,
): SourcesFigures {
  return {
    files: sources.files,
    processing: sources.processing,
    failed: sources.failed,
    restricted: can(project, "file.restricted_count") ? (sources.restricted ?? null) : null,
  };
}

/** The People section: who runs the project and how many are in it. */
export interface PeopleFigures {
  managers: string[];
  members: number;
  groups: number;
}

export function peopleFigures(
  members: readonly ProjectMemberView[],
  groups: readonly ProjectGroupGrant[] | undefined,
  unnamed: string,
): PeopleFigures {
  return {
    managers: members
      .filter((m) => m.role === "manager")
      .map((m) => m.displayName ?? m.email ?? unnamed)
      .sort((a, b) => a.localeCompare(b)),
    members: members.length,
    groups: groups?.length ?? 0,
  };
}
